// client/src/hooks/useCategorias.js - VERSÃO 100% COMPLETA E CORRIGIDA
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react'; // ✅ IMPORT CORRIGIDO
import api from '../services/api';
import useAlert from './useAlert';

const CATEGORIAS_QUERY_KEY = 'categorias';
const CATEGORIA_DETAIL_QUERY_KEY = 'categoria';
const TODAS_CATEGORIAS_QUERY_KEY = 'todas-categorias';

const sanitizeParams = (params) => {
  const sanitized = {};
  
  Object.keys(params).forEach(key => {
    const value = params[key];
    
    if (value !== undefined && value !== null && value !== '' && value !== 'todos') {
      if (typeof value === 'boolean') {
        sanitized[key] = value.toString();
      } else {
        sanitized[key] = value;
      }
    }
  });
  
  return sanitized;
};

// ✅ CORREÇÃO CRÍTICA: Processar resposta da API com garantia de IDs
const processApiResponse = (responseData) => {
  if (!responseData) {
    return { success: false, data: [], count: 0, totalCount: 0, pagination: {} };
  }

  let rawData = [];
  let success = true;
  let count = 0;
  let totalCount = 0;
  let pagination = {};

  if (responseData.success !== undefined && responseData.data !== undefined) {
    success = responseData.success;
    rawData = Array.isArray(responseData.data) ? responseData.data : [];
    count = responseData.count || rawData.length;
    totalCount = responseData.totalCount || count;
    pagination = responseData.pagination || {};
  } else if (Array.isArray(responseData)) {
    success = true;
    rawData = responseData;
    count = responseData.length;
    totalCount = responseData.length;
    pagination = {};
  } else if (responseData.data && Array.isArray(responseData.data)) {
    success = true;
    rawData = responseData.data;
    count = rawData.length;
    totalCount = responseData.totalCount || count;
    pagination = responseData.pagination || {};
  }

  // ✅ CORREÇÃO CRÍTICA: Normalizar cada categoria para garantir _id
  const normalizedData = rawData
    .map(item => {
      if (!item) return null;
      
      // Garantir que _id exista
      const id = item._id || item.id;
      
      if (!id) {
        console.error('❌ [API_RESPONSE] Item sem ID detectado:', item);
        return null; // Filtrar itens inválidos
      }
      
      return {
        ...item,
        _id: id,
        id: id
      };
    })
    .filter(Boolean); // Remover null

  console.log('✅ [API_RESPONSE] Dados normalizados:', {
    recebidos: rawData.length,
    validos: normalizedData.length,
    invalidos: rawData.length - normalizedData.length
  });

  return {
    success,
    data: normalizedData,
    count: normalizedData.length,
    totalCount,
    pagination
  };
};

// ✅ Helper para normalizar categorias
const normalizeCategoria = (categoria) => {
  if (!categoria) return null;
  
  const id = categoria._id || categoria.id;
  
  if (!id) {
    console.error('❌ [NORMALIZE] Categoria sem ID:', categoria);
    return null;
  }
  
  return {
    _id: id,
    id: id,
    nome: categoria.nome || 'Categoria sem nome',
    descricao: categoria.descricao || '',
    tipo: categoria.tipo || 'geral',
    ordem: categoria.ordem || 999,
    ativo: categoria.ativo !== false,
    visivelParaCliente: categoria.visivelParaCliente !== false,
    imagem: categoria.imagem || null,
    imagemUrl: categoria.imagemUrl || null,
    categoriaPai: categoria.categoriaPai || null,
    metadata: categoria.metadata || {
      quantidadeItens: 0,
      ultimaAtualizacao: null,
      tags: []
    },
    createdAt: categoria.createdAt,
    updatedAt: categoria.updatedAt
  };
};

export const useCategorias = (accountId, params = {}) => {
  return useQuery({
    queryKey: [CATEGORIAS_QUERY_KEY, accountId, params],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [USE_CATEGORIAS] Account ID não fornecido');
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {},
          error: 'Account ID é obrigatório'
        };
      }

      const backendFilters = {};
      
      if (params.search) backendFilters.search = params.search;
      if (params.tipo) backendFilters.tipo = params.tipo;
      if (params.ativo !== undefined && params.ativo !== '') {
        backendFilters.ativo = params.ativo;
      }

      const cleanParams = sanitizeParams({
        ...params,
        ...backendFilters
      });

      console.log('🔧 [USE_CATEGORIAS] Buscando categorias:', {
        accountId,
        params: cleanParams,
        backendFilters
      });

      try {
        const response = await api.get('/categorias', { 
          params: cleanParams,
          timeout: 15000
        });
        
        console.log('✅ [USE_CATEGORIAS] Resposta bruta:', {
          success: response.data?.success,
          count: response.data?.count,
          dataLength: response.data?.data?.length
        });
        
        return processApiResponse(response.data);
        
      } catch (error) {
        console.error('❌ [USE_CATEGORIAS] Erro:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data
        });
        
        return {
          success: false,
          data: [],
          count: 0,
          totalCount: 0,
          pagination: {
            page: parseInt(cleanParams.page) || 1,
            limit: parseInt(cleanParams.limit) || 10,
            totalPages: 0,
            totalCount: 0,
            hasNext: false,
            hasPrev: false
          },
          error: error.message
        };
      }
    },
    enabled: !!accountId,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    retry: 1,
  });
};

// ✅ CORREÇÃO: useCategoriasParaFormulario com normalização
export const useCategoriasParaFormulario = (accountId, tipo = null, options = {}) => {
  const { setAlert } = useAlert();

  const fetchCategoriasParaFormulario = async () => {
    if (!accountId) {
      console.warn('❌ [USE_CATEGORIAS_FORMULARIO] Account ID não fornecido');
      return [];
    }
    
    console.log('📋 [USE_CATEGORIAS_FORMULARIO] Buscando categorias para formulário:', {
      accountId,
      tipo
    });
    
    try {
      const params = {
        account: accountId,
        ativo: 'true',
        limit: 500,
        sort: 'ordem,nome',
        ...(tipo && tipo !== 'todos' && tipo !== 'todas' && { tipo })
      };

      console.log('🔧 [USE_CATEGORIAS_FORMULARIO] Parâmetros enviados:', params);

      const response = await api.get('/categorias', {
        params,
        timeout: 15000
      });
      
      console.log('✅ [USE_CATEGORIAS_FORMULARIO] Resposta completa:', {
        success: response.data?.success,
        count: response.data?.count,
        dataLength: response.data?.data?.length
      });
      
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Erro ao buscar categorias');
      }
      
      let categorias = response.data?.data || [];
      
      if (tipo && tipo !== 'todos' && tipo !== 'todas') {
        categorias = categorias.filter(categoria => 
          categoria.tipo === tipo || categoria.tipo === 'geral'
        );
        console.log(`📊 [USE_CATEGORIAS_FORMULARIO] Categorias filtradas para ${tipo}:`, categorias.length);
      }
      
      // ✅ CORREÇÃO: Normalizar categorias e filtrar inválidas
      const categoriasNormalizadas = categorias
        .map(normalizeCategoria)
        .filter(Boolean);
      
      categoriasNormalizadas.sort((a, b) => {
        if (a.ordem !== b.ordem) {
          return (a.ordem || 999) - (b.ordem || 999);
        }
        return (a.nome || '').localeCompare(b.nome || '');
      });
      
      console.log('✅ [USE_CATEGORIAS_FORMULARIO] Categorias processadas:', {
        recebidas: categorias.length,
        validas: categoriasNormalizadas.length,
        invalidas: categorias.length - categoriasNormalizadas.length
      });
      
      return categoriasNormalizadas;
      
    } catch (error) {
      console.error('❌ [USE_CATEGORIAS_FORMULARIO] Erro:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      setAlert('Erro ao carregar categorias', 'danger');
      return [];
    }
  };

  return useQuery({
    queryKey: ['categorias-formulario', accountId, tipo],
    queryFn: fetchCategoriasParaFormulario,
    enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 10 * 60 * 1000,
    retry: 2,
    ...options
  });
};

// ✅ CORREÇÃO: Implementação completa de useTodasCategorias
export const useTodasCategorias = (accountId, options = {}) => {
  const { setAlert } = useAlert();

  const fetchTodasCategorias = async () => {
    if (!accountId) {
      console.warn('⚠️ [USE_TODAS_CATEGORIAS] Account ID não fornecido');
      return { data: [], count: 0, totalCount: 0, pagination: {} };
    }

    console.log('🔍 [USE_TODAS_CATEGORIAS] Buscando todas as categorias:', {
      accountId,
      tipo: options.tipo,
      includeInactive: options.includeInactive
    });

    try {
      const params = {
        account: accountId,
        limit: 500,
        sort: 'ordem,nome',
        ...(options.tipo && options.tipo !== 'todos' && options.tipo !== 'todas' && { tipo: options.tipo })
      };

      // Se não quiser incluir inativas, filtra apenas ativas
      if (!options.includeInactive) {
        params.ativo = 'true';
      }

      const response = await api.get('/categorias', {
        params,
        timeout: 15000
      });

      console.log('✅ [USE_TODAS_CATEGORIAS] Resposta:', {
        success: response.data?.success,
        count: response.data?.count,
        dataLength: response.data?.data?.length
      });

      // Processar a resposta para garantir a estrutura correta
      const processedData = processApiResponse(response.data);
      
      return {
        data: processedData.data || [],
        count: processedData.count || 0,
        totalCount: processedData.totalCount || 0,
        pagination: processedData.pagination || {}
      };

    } catch (error) {
      console.error('❌ [USE_TODAS_CATEGORIAS] Erro:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });

      setAlert('Erro ao carregar categorias', 'danger');
      
      return {
        data: [],
        count: 0,
        totalCount: 0,
        pagination: {}
      };
    }
  };

  return useQuery({
    queryKey: [TODAS_CATEGORIAS_QUERY_KEY, accountId, options.tipo, options.includeInactive],
    queryFn: fetchTodasCategorias,
    enabled: !!accountId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 5 * 60 * 1000,
    retry: 2,
    ...options
  });
};

// ✅ Implementação de useCategoriasPorTipo
export const useCategoriasPorTipo = (accountId, tipo, options = {}) => {
  return useTodasCategorias(accountId, { 
    ...options, 
    tipo,
    includeInactive: options.includeInactive || false 
  });
};

export const useCategoriasServico = (accountId, options = {}) => {
  return useCategoriasPorTipo(accountId, 'servico', options);
};

export const useCategoriasProduto = (accountId, options = {}) => {
  return useCategoriasPorTipo(accountId, 'produto', options);
};

export const useCategoriasRecurso = (accountId, options = {}) => {
  return useCategoriasPorTipo(accountId, 'recurso', options);
};

export const useCategoriasGeral = (accountId, options = {}) => {
  return useCategoriasPorTipo(accountId, 'geral', options);
};

export const useCategoriasCurso = (accountId, options = {}) => {
  return useCategoriasPorTipo(accountId, 'curso', options);
};

// ✅ Implementação de useArvoreCategorias
export const useArvoreCategorias = (accountId, tipo = 'geral') => {
  const { data: categorias, isLoading, error } = useTodasCategorias(accountId, { tipo });

  const arvore = useMemo(() => {
    if (!categorias?.data || !Array.isArray(categorias.data)) return [];
    
    const categoriasMap = new Map();
    const raiz = [];
    
    // Primeiro, mapear todas as categorias por ID
    categorias.data.forEach(cat => {
      categoriasMap.set(cat._id, { ...cat, subcategorias: [] });
    });
    
    // Depois, organizar a hierarquia
    categorias.data.forEach(cat => {
      const categoriaComSub = categoriasMap.get(cat._id);
      if (cat.categoriaPai && categoriasMap.has(cat.categoriaPai)) {
        const pai = categoriasMap.get(cat.categoriaPai);
        pai.subcategorias.push(categoriaComSub);
      } else {
        raiz.push(categoriaComSub);
      }
    });
    
    return raiz;
  }, [categorias]);

  return {
    data: arvore,
    isLoading,
    error
  };
};

// ✅ Implementação de useCaminhoCategoria
export const useCaminhoCategoria = (categoriaId) => {
  const { data: todasCategorias, isLoading, error } = useTodasCategorias();

  const caminho = useMemo(() => {
    if (!categoriaId || !todasCategorias?.data || !Array.isArray(todasCategorias.data)) {
      return [];
    }
    
    const categoriasMap = new Map();
    todasCategorias.data.forEach(cat => categoriasMap.set(cat._id, cat));
    
    const path = [];
    let currentId = categoriaId;
    
    while (currentId && categoriasMap.has(currentId)) {
      const current = categoriasMap.get(currentId);
      path.unshift(current);
      currentId = current.categoriaPai;
    }
    
    return path;
  }, [categoriaId, todasCategorias]);

  return {
    data: caminho,
    isLoading,
    error
  };
};

// ✅ Implementação de useEstatisticasCategorias
export const useEstatisticasCategorias = (accountId) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: ['estatisticas-categorias', accountId],
    queryFn: async () => {
      if (!accountId) {
        console.warn('⚠️ [USE_ESTATISTICAS_CATEGORIAS] Account ID não fornecido');
        return {};
      }

      try {
        const { data } = await api.get('/categorias/estatisticas', {
          params: { account: accountId }
        });

        return data?.data || {};
      } catch (error) {
        console.error('❌ [USE_ESTATISTICAS_CATEGORIAS] Erro:', error);
        setAlert('Erro ao carregar estatísticas de categorias', 'danger');
        return {};
      }
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000,
    retry: 1
  });
};

// ✅ Implementação de useCategoria
export const useCategoria = (categoriaId, options = {}) => {
  const { setAlert } = useAlert();

  return useQuery({
    queryKey: [CATEGORIA_DETAIL_QUERY_KEY, categoriaId],
    queryFn: async () => {
      if (!categoriaId) {
        console.warn('⚠️ [USE_CATEGORIA] Categoria ID não fornecido');
        return null;
      }

      try {
        const { data } = await api.get(`/categorias/${categoriaId}`);
        
        const categoriaData = data?.data || data;
        return categoriaData ? normalizeCategoria(categoriaData) : null;
      } catch (error) {
        console.error('❌ [USE_CATEGORIA] Erro:', error);
        
        if (error.response?.status !== 404) {
          setAlert('Erro ao carregar categoria', 'danger');
        }
        
        return null;
      }
    },
    enabled: !!categoriaId && (options.enabled !== undefined ? options.enabled : true),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    ...options
  });
};

// ✅ FUNÇÃO AUXILIAR DE VALIDAÇÃO DE ID
const validateCategoriaId = (categoriaId, context) => {
  if (!categoriaId) {
    const errorMsg = `❌ [VALIDATE_CATEGORIA_ID] ID não fornecido em ${context}`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  
  if (typeof categoriaId !== 'string' || !categoriaId.match(/^[0-9a-fA-F]{24}$/)) {
    const errorMsg = `❌ [VALIDATE_CATEGORIA_ID] ID inválido em ${context}: ${categoriaId}`;
    console.error(errorMsg);
    throw new Error(errorMsg);
  }
  
  return categoriaId;
};

export const useMutateCategorias = (accountId) => {
  const queryClient = useQueryClient();
  const { setAlert } = useAlert();

  const invalidateCaches = (categoriaId = null) => {
    const queriesToInvalidate = [
      CATEGORIAS_QUERY_KEY,
      TODAS_CATEGORIAS_QUERY_KEY,
      'categorias-tipo',
      'categorias-formulario',
      'arvore-categorias',
      'estatisticas-categorias'
    ];

    queriesToInvalidate.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    });

    if (categoriaId) {
      queryClient.invalidateQueries({ queryKey: [CATEGORIA_DETAIL_QUERY_KEY, categoriaId] });
      queryClient.invalidateQueries({ queryKey: ['caminho-categoria', categoriaId] });
    }

    console.log('🔄 [USE_MUTATE_CATEGORIAS] Caches invalidados');
  };

  const handleError = (error, defaultMessage) => {
    console.error('❌ [USE_MUTATE_CATEGORIAS] Erro:', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    });
    
    const errorMessage = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       defaultMessage;
    
    setAlert(errorMessage, 'danger');
    throw new Error(errorMessage);
  };

  const handleSuccess = (message, categoriaId = null) => {
    invalidateCaches(categoriaId);
    setAlert(message, 'success');
  };

  const createCategoria = useMutation({
    mutationFn: async (categoriaData) => {
      console.log('🆕 [USE_MUTATE_CATEGORIAS] Criando categoria:', categoriaData);
      const response = await api.post('/categorias', categoriaData);
      return response.data;
    },
    onSuccess: (response) => {
      handleSuccess('Categoria criada com sucesso!', response.data?._id);
    },
    onError: (error) => handleError(error, 'Erro ao criar categoria'),
  });

  const updateCategoria = useMutation({
    mutationFn: async ({ id, data }) => {
      const validId = validateCategoriaId(id, 'UPDATE_CATEGORIA');
      console.log('✏️ [USE_MUTATE_CATEGORIAS] Atualizando categoria:', { id: validId, data });
      const response = await api.put(`/categorias/${validId}`, data);
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Categoria atualizada com sucesso!', variables.id);
    },
    onError: (error) => handleError(error, 'Erro ao atualizar categoria'),
  });

  const deleteCategoria = useMutation({
    mutationFn: async (categoriaId) => {
      const validId = validateCategoriaId(categoriaId, 'DELETE_CATEGORIA');
      console.log('🗑️ [USE_MUTATE_CATEGORIAS] Removendo categoria:', validId);
      const response = await api.delete(`/categorias/${validId}`);
      return response.data;
    },
    onSuccess: (response, categoriaId) => {
      handleSuccess('Categoria removida com sucesso!', categoriaId);
    },
    onError: (error) => {
      console.error('❌ [USE_MUTATE_CATEGORIAS] Erro detalhado:', error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         error.message || 
                         'Erro ao remover categoria';
      setAlert(errorMessage, 'danger');
    },
  });

  const uploadImagemCategoria = useMutation({
    mutationFn: async ({ categoriaId, imagem }) => {
      const validId = validateCategoriaId(categoriaId, 'UPLOAD_IMAGEM_CATEGORIA');
      console.log('🖼️ [USE_MUTATE_CATEGORIAS] Upload imagem para categoria:', validId);
      
      const formData = new FormData();
      formData.append('imagem', imagem);
      
      const response = await api.post(`/categorias/${validId}/imagem`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Imagem da categoria atualizada com sucesso!', variables.categoriaId);
    },
    onError: (error) => handleError(error, 'Erro ao fazer upload da imagem'),
  });

  const removeImagemCategoria = useMutation({
    mutationFn: async (categoriaId) => {
      const validId = validateCategoriaId(categoriaId, 'REMOVE_IMAGEM_CATEGORIA');
      console.log('🖼️ [USE_MUTATE_CATEGORIAS] Removendo imagem da categoria:', validId);
      const response = await api.delete(`/categorias/${validId}/imagem`);
      return response.data;
    },
    onSuccess: (response, categoriaId) => {
      handleSuccess('Imagem da categoria removida com sucesso!', categoriaId);
    },
    onError: (error) => handleError(error, 'Erro ao remover imagem'),
  });

  const toggleStatusCategoria = useMutation({
    mutationFn: async ({ id, ativo }) => {
      const validId = validateCategoriaId(id, 'TOGGLE_STATUS_CATEGORIA');
      console.log('🔄 [USE_MUTATE_CATEGORIAS] Alternando status da categoria:', { id: validId, ativo });
      const response = await api.patch(`/categorias/${validId}/alternar-status`, { ativo });
      return response.data;
    },
    onSuccess: (response, variables) => {
      const message = variables.ativo ? 'Categoria ativada com sucesso!' : 'Categoria desativada com sucesso!';
      handleSuccess(message, variables.id);
    },
    onError: (error) => handleError(error, 'Erro ao alternar status da categoria'),
  });

  const moveCategoria = useMutation({
    mutationFn: async ({ id, categoriaPaiId }) => {
      const validId = validateCategoriaId(id, 'MOVE_CATEGORIA');
      console.log('🚚 [USE_MUTATE_CATEGORIAS] Movendo categoria:', { id: validId, categoriaPaiId });
      const response = await api.patch(`/categorias/${validId}/mover`, { categoriaPaiId });
      return response.data;
    },
    onSuccess: (response, variables) => {
      handleSuccess('Categoria movida com sucesso!', variables.id);
    },
    onError: (error) => handleError(error, 'Erro ao mover categoria'),
  });

  const reordenarCategorias = useMutation({
    mutationFn: async (categorias) => {
      console.log('📋 [USE_MUTATE_CATEGORIAS] Reordenando categorias:', categorias);
      const response = await api.patch('/categorias/reordenar', { categorias });
      return response.data;
    },
    onSuccess: () => {
      handleSuccess('Categorias reordenadas com sucesso!');
    },
    onError: (error) => handleError(error, 'Erro ao reordenar categorias'),
  });

  const atualizarContagemLote = useMutation({
    mutationFn: async () => {
      console.log('🔢 [USE_MUTATE_CATEGORIAS] Atualizando contagem em lote');
      const response = await api.patch('/categorias/atualizar-contagem-lote');
      return response.data;
    },
    onSuccess: () => {
      handleSuccess('Contagem de itens atualizada em lote com sucesso!');
    },
    onError: (error) => handleError(error, 'Erro ao atualizar contagem em lote'),
  });

  const isLoading = {
    create: createCategoria.isPending,
    update: updateCategoria.isPending,
    delete: deleteCategoria.isPending,
    uploadImage: uploadImagemCategoria.isPending,
    removeImage: removeImagemCategoria.isPending,
    toggleStatus: toggleStatusCategoria.isPending,
    move: moveCategoria.isPending,
    reordenar: reordenarCategorias.isPending,
    atualizarContagemLote: atualizarContagemLote.isPending
  };

  const isPending = Object.values(isLoading).some(status => status);

  return {
    createCategoria,
    updateCategoria,
    deleteCategoria,
    uploadImagemCategoria,
    removeImagemCategoria,
    toggleStatusCategoria,
    moveCategoria,
    reordenarCategorias,
    atualizarContagemLote,
    isPending,
    isLoading,
    invalidateCaches,
    forceRefetch: () => invalidateCaches(),
  };
};

export const useCategoriaManagement = (accountId) => {
  const categorias = useCategorias(accountId);
  const categoriasFormulario = useCategoriasParaFormulario(accountId);
  const estatisticas = useEstatisticasCategorias(accountId);
  const mutations = useMutateCategorias(accountId);

  return {
    categorias: categorias.data?.data || [],
    categoriasFormulario: categoriasFormulario.data || [],
    todasCategorias: categoriasFormulario.data || [],
    estatisticas: estatisticas.data || {},
    isLoading: categorias.isLoading || categoriasFormulario.isLoading,
    isError: categorias.isError || categoriasFormulario.isError,
    error: categorias.error || categoriasFormulario.error,
    isLoadingEstatisticas: estatisticas.isLoading,
    isErrorEstatisticas: estatisticas.isError,
    errorEstatisticas: estatisticas.error,
    ...mutations,
    refetch: () => {
      categorias.refetch();
      categoriasFormulario.refetch();
      estatisticas.refetch();
    },
    refetchAll: () => {
      categorias.refetch();
      categoriasFormulario.refetch();
      estatisticas.refetch();
      mutations.forceRefetch();
    }
  };
};

export const useCategoriaForm = (accountId, tipo = null) => {
  const { 
    data: categorias, 
    isLoading, 
    error 
  } = useCategoriasParaFormulario(accountId, tipo);
  
  const { createCategoria, updateCategoria, isLoading: mutationLoading } = useMutateCategorias(accountId);

  return {
    categorias: categorias || [],
    isLoading,
    error,
    createCategoria,
    updateCategoria,
    isSaving: mutationLoading.create || mutationLoading.update,
    getCategoriasPorTipo: (tipoFiltro) => {
      if (!categorias) return [];
      return categorias.filter(cat => cat.tipo === tipoFiltro);
    },
    getCategoriasRaiz: () => {
      if (!categorias) return [];
      return categorias.filter(cat => !cat.categoriaPai);
    },
    getSubcategorias: (categoriaPaiId) => {
      if (!categorias) return [];
      return categorias.filter(cat => {
        const paiId = cat.categoriaPai?._id || cat.categoriaPai;
        return paiId === categoriaPaiId;
      });
    },
    getCategoriaById: (id) => {
      if (!categorias) return null;
      return categorias.find(cat => cat._id === id);
    }
  };
};

export const useCategoriaServico = (accountId, options = {}) => ({
  ...useCategoriasServico(accountId, options),
  ...useMutateCategorias(accountId)
});

export const useCategoriaProduto = (accountId, options = {}) => ({
  ...useCategoriasProduto(accountId, options),
  ...useMutateCategorias(accountId)
});

export const useCategoriaRecurso = (accountId, options = {}) => ({
  ...useCategoriasRecurso(accountId, options),
  ...useMutateCategorias(accountId)
});

export const useCategoriaGeral = (accountId, options = {}) => ({
  ...useCategoriasGeral(accountId, options),
  ...useMutateCategorias(accountId)
});

export default useCategorias;